import { TValueProvider } from '@commenda/types';
import { useRef, useState } from 'react';
import { getDefaultConstObj } from '../editors/helpers';

export interface CustomVariableRow {
  id: string;
  name: string;
  value: TValueProvider;
}

interface UseSysEmailStepVariablesParams {
  stepId: string;
  templateId?: string;
  contentValueMapping?: Record<string, TValueProvider>;
  parsedVariables: string[];
  isLoadingVariables: boolean;
  onChange: (contentValueMapping: Record<string, TValueProvider>) => void;
}

// Owns the email step's variable-mapping state: which parsed template variables are checked (the
// MultiSelect), their values, and any custom variables typed by hand. Mirrors the persisted
// contentValueMapping and re-seeds only when the template (or its parsed variables) changes.
export const useSysEmailStepVariables = ({
  stepId,
  templateId,
  contentValueMapping,
  parsedVariables,
  isLoadingVariables,
  onChange,
}: UseSysEmailStepVariablesParams) => {
  const [selectedTemplateVars, setSelectedTemplateVars] = useState<string[]>(
    []
  );
  const [templateValues, setTemplateValues] = useState<
    Record<string, TValueProvider>
  >({});
  const [customRows, setCustomRows] = useState<CustomVariableRow[]>([]);
  const seededForRef = useRef<string | null>(null);
  const seededSelectionRef = useRef<string[]>([]);

  const seedKey = `${stepId}:${templateId ?? ''}:${parsedVariables.join('|')}`;
  if (!isLoadingVariables && seededForRef.current !== seedKey) {
    seededForRef.current = seedKey;
    const mapping = contentValueMapping ?? {};
    const parsedSet = new Set(parsedVariables);
    const selected = Object.keys(mapping).filter((name) => parsedSet.has(name));
    const values: Record<string, TValueProvider> = {};
    selected.forEach((name) => {
      values[name] = mapping[name] ?? getDefaultConstObj();
    });
    const custom = Object.keys(mapping)
      .filter((name) => !parsedSet.has(name))
      .map((name) => ({
        id: crypto.randomUUID(),
        name,
        value: mapping[name] ?? getDefaultConstObj(),
      }));
    seededSelectionRef.current = selected;
    setSelectedTemplateVars(selected);
    setTemplateValues(values);
    setCustomRows(custom);
  }

  // Rebuild contentValueMapping from the checked template variables + custom rows (skip blank names).
  const persist = (
    selected: string[],
    values: Record<string, TValueProvider>,
    custom: CustomVariableRow[]
  ) => {
    const mapping: Record<string, TValueProvider> = {};
    selected.forEach((name) => {
      mapping[name] = values[name] ?? getDefaultConstObj();
    });
    custom.forEach((row) => {
      const key = row.name.trim();
      if (key) mapping[key] = row.value;
    });
    onChange(mapping);
  };

  const onSelectTemplateVars = (next: string[]) => {
    const values = { ...templateValues };
    next.forEach((name) => {
      if (!(name in values)) values[name] = getDefaultConstObj();
    });
    setSelectedTemplateVars(next);
    setTemplateValues(values);
    persist(next, values, customRows);
  };

  const setTemplateValue = (name: string, value: TValueProvider) => {
    const values = { ...templateValues, [name]: value };
    setTemplateValues(values);
    persist(selectedTemplateVars, values, customRows);
  };

  const addCustomRow = () => {
    setCustomRows([
      ...customRows,
      { id: crypto.randomUUID(), name: '', value: getDefaultConstObj() },
    ]);
  };

  const setCustomName = (id: string, name: string) => {
    const next = customRows.map((row) =>
      row.id === id ? { ...row, name } : row
    );
    setCustomRows(next);
    persist(selectedTemplateVars, templateValues, next);
  };

  const setCustomValue = (id: string, value: TValueProvider) => {
    const next = customRows.map((row) =>
      row.id === id ? { ...row, value } : row
    );
    setCustomRows(next);
    persist(selectedTemplateVars, templateValues, next);
  };

  const removeCustomRow = (id: string) => {
    const next = customRows.filter((row) => row.id !== id);
    setCustomRows(next);
    persist(selectedTemplateVars, templateValues, next);
  };

  return {
    seedKey,
    initialSelectedTemplateVars: seededSelectionRef.current,
    selectedTemplateVars,
    templateValues,
    customRows,
    onSelectTemplateVars,
    setTemplateValue,
    addCustomRow,
    setCustomName,
    setCustomValue,
    removeCustomRow,
  };
};
